import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

export default class extends Controller {
  static targets = ["step", "indicator", "fileList", "fileInput", "fileError", "dropZone", "submitButton"]
  static values = {
    currentStep: { type: Number, default: 1 },
    totalSteps: { type: Number, default: 3 },
    maxFiles: { type: Number, default: 5 },
    directUploadUrl: String
  }

  connect() {
    this.uploads = [] // Track { file, signedId, status, progress }
    this.showStep(this.currentStepValue)
  }

  // -- Step Navigation --

  next() {
    if (!this.validateCurrentStep()) return
    if (this.currentStepValue < this.totalStepsValue) {
      this.currentStepValue++
      this.showStep(this.currentStepValue)
    }
  }

  back() {
    if (this.currentStepValue > 1) {
      this.currentStepValue--
      this.showStep(this.currentStepValue)
    }
  }

  showStep(step) {
    this.stepTargets.forEach((el, index) => {
      el.classList.toggle("hidden", index + 1 !== step)
    })

    this.indicatorTargets.forEach((el, index) => {
      const stepNum = index + 1
      const dot = el.querySelector("[data-dot]")
      const line = el.querySelector("[data-line]")
      const label = el.querySelector("[data-label]")

      if (stepNum < step) {
        dot?.classList.remove("border-brand", "bg-brand/10", "text-brand", "border-default", "bg-neutral-secondary", "text-body-subtle")
        dot?.classList.add("border-brand", "bg-brand", "text-white")
        line?.classList.remove("bg-neutral-secondary-strong")
        line?.classList.add("bg-brand")
        label?.classList.remove("text-body-subtle")
        label?.classList.add("text-brand")
      } else if (stepNum === step) {
        dot?.classList.remove("border-brand", "bg-brand", "text-white", "border-default", "bg-neutral-secondary", "text-body-subtle")
        dot?.classList.add("border-brand", "bg-brand/10", "text-brand")
        line?.classList.remove("bg-brand")
        line?.classList.add("bg-neutral-secondary-strong")
        label?.classList.remove("text-body-subtle")
        label?.classList.add("text-brand")
      } else {
        dot?.classList.remove("border-brand", "bg-brand", "text-white", "bg-brand/10", "text-brand")
        dot?.classList.add("border-default", "bg-neutral-secondary", "text-body-subtle")
        line?.classList.remove("bg-brand")
        line?.classList.add("bg-neutral-secondary-strong")
        label?.classList.remove("text-brand")
        label?.classList.add("text-body-subtle")
      }
    })
  }

  // -- Validation --

  validateCurrentStep() {
    if (this.currentStepValue === 1) {
      const textarea = this.element.querySelector("[name='canvas[business_idea]']")
      if (!textarea || textarea.value.trim().length < 10) {
        textarea?.focus()
        this.showValidationError(textarea, "Please describe your business idea (at least 10 characters)")
        return false
      }
      this.clearValidationError(textarea)
    }

    if (this.currentStepValue === 2) {
      const nameInput = this.element.querySelector("[name='canvas[name]']")
      const typeInput = this.element.querySelector("[name='canvas[canvas_type]']:checked")

      if (!nameInput || nameInput.value.trim() === "") {
        nameInput?.focus()
        this.showValidationError(nameInput, "Please give your canvas a name")
        return false
      }
      this.clearValidationError(nameInput)

      if (!typeInput) {
        this.showValidationError(
          this.element.querySelector("[data-canvas-type-error]"),
          "Please select a canvas type"
        )
        return false
      }
    }

    return true
  }

  showValidationError(element, message) {
    if (!element) return
    let errorEl = element.parentElement?.querySelector("[data-validation-error]")
    if (!errorEl) {
      errorEl = document.createElement("p")
      errorEl.setAttribute("data-validation-error", "")
      errorEl.className = "mt-1 text-sm text-red-600 dark:text-red-400"
      element.parentElement?.appendChild(errorEl)
    }
    errorEl.textContent = message
  }

  clearValidationError(element) {
    if (!element) return
    const errorEl = element.parentElement?.querySelector("[data-validation-error]")
    errorEl?.remove()
  }

  // -- Drag and Drop --

  dragOver(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.add("border-brand", "bg-brand/5")
  }

  dragLeave(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.remove("border-brand", "bg-brand/5")
  }

  drop(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.remove("border-brand", "bg-brand/5")
    const files = Array.from(event.dataTransfer.files)
    this.addFiles(files)
  }

  // -- File Selection --

  filesSelected() {
    const files = Array.from(this.fileInputTarget.files)
    this.addFiles(files)
    // Reset input so the same file can be re-selected
    this.fileInputTarget.value = ""
  }

  addFiles(files) {
    this.clearFileError()

    const remaining = this.maxFilesValue - this.uploads.length
    if (remaining <= 0) {
      this.showFileError(`Maximum ${this.maxFilesValue} files allowed`)
      return
    }

    const filesToAdd = files.slice(0, remaining)
    if (files.length > remaining) {
      this.showFileError(`Only ${remaining} more file${remaining === 1 ? '' : 's'} can be added (max ${this.maxFilesValue})`)
    }

    filesToAdd.forEach(file => this.uploadFile(file))
  }

  // -- Direct Upload --

  uploadFile(file) {
    const uploadEntry = {
      file,
      signedId: null,
      status: "uploading",
      progress: 0,
      id: crypto.randomUUID()
    }
    this.uploads.push(uploadEntry)
    this.renderFileList()

    const url = this.directUploadUrlValue
    const upload = new DirectUpload(file, url, {
      directUploadWillStoreFileWithXHR: (request) => {
        request.upload.addEventListener("progress", (event) => {
          uploadEntry.progress = Math.round((event.loaded / event.total) * 100)
          this.renderFileList()
        })
      }
    })

    upload.create((error, blob) => {
      if (error) {
        uploadEntry.status = "error"
        uploadEntry.error = error
        this.renderFileList()
      } else {
        uploadEntry.signedId = blob.signed_id
        uploadEntry.status = "complete"
        uploadEntry.progress = 100
        this.addHiddenField(blob.signed_id, uploadEntry.id)
        this.renderFileList()
      }
    })
  }

  addHiddenField(signedId, uploadId) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = "documents[]"
    input.value = signedId
    input.dataset.uploadId = uploadId
    this.element.querySelector("form").appendChild(input)
  }

  removeFile(event) {
    const id = event.currentTarget.dataset.uploadId
    this.uploads = this.uploads.filter(u => u.id !== id)
    // Remove corresponding hidden field
    const hidden = this.element.querySelector(`input[data-upload-id="${id}"]`)
    hidden?.remove()
    this.clearFileError()
    this.renderFileList()
  }

  // -- File List Rendering --

  renderFileList() {
    if (!this.hasFileListTarget) return

    if (this.uploads.length === 0) {
      this.fileListTarget.innerHTML = ""
      return
    }

    const html = this.uploads.map(upload => {
      const isError = upload.status === "error"
      const isUploading = upload.status === "uploading"
      const isComplete = upload.status === "complete"

      return `
        <div class="relative flex items-center justify-between p-3 bg-neutral-secondary rounded-base border ${isError ? 'border-red-300 dark:border-red-700' : 'border-default'}">
          <div class="flex items-center gap-2 min-w-0 flex-1">
            ${isComplete ? this.checkIcon() : isError ? this.errorIcon() : this.fileIcon()}
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-2">
                <span class="text-sm ${isError ? 'text-red-600 dark:text-red-400' : 'text-body'} truncate">${upload.file.name}</span>
                <span class="text-xs text-body-subtle shrink-0">${this.formatFileSize(upload.file.size)}</span>
              </div>
              ${isUploading ? `
                <div class="mt-1 w-full bg-neutral-tertiary rounded-full h-1.5 overflow-hidden">
                  <div class="bg-brand h-full rounded-full transition-all duration-150 ease-out" style="width: ${upload.progress}%"></div>
                </div>
              ` : ''}
              ${isError ? `<p class="text-xs text-red-500 mt-0.5">Upload failed. Please try again.</p>` : ''}
            </div>
          </div>
          <button type="button" data-action="click->canvas-wizard#removeFile" data-upload-id="${upload.id}"
                  class="p-1 rounded hover:bg-neutral-tertiary text-body-subtle hover:text-heading shrink-0 ml-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
          </button>
        </div>
      `
    }).join("")

    this.fileListTarget.innerHTML = html
  }

  // -- Icons --

  fileIcon() {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="shrink-0 text-body-subtle animate-pulse"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/></svg>`
  }

  checkIcon() {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="shrink-0 text-green-500"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><path d="m9 11 3 3L22 4"/></svg>`
  }

  errorIcon() {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="shrink-0 text-red-500"><circle cx="12" cy="12" r="10"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/></svg>`
  }

  // -- Helpers --

  showFileError(message) {
    if (!this.hasFileErrorTarget) return
    this.fileErrorTarget.textContent = message
    this.fileErrorTarget.classList.remove("hidden")
  }

  clearFileError() {
    if (!this.hasFileErrorTarget) return
    this.fileErrorTarget.textContent = ""
    this.fileErrorTarget.classList.add("hidden")
  }

  formatFileSize(bytes) {
    if (bytes < 1024) return bytes + " B"
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB"
    return (bytes / (1024 * 1024)).toFixed(1) + " MB"
  }
}
